import { ArrowLeft, Calendar, Check, ClipboardListIcon, ExternalLinkIcon, X } from "lucide-react";
import { Badge } from "@component/badge";
import { cn } from "@util/tailwind";
import { convertToLowerSnakeCase, getApplicantStatusBadge } from "@util/string";
import { parseFormat } from "@util/date";
import { JobApplicantItem } from "@type/job";
import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { getUserInterviewSubmission } from "@service/assessment";
import { FormQuestion, UserInterview, UserSubmissionJob } from "@type/assessments";
import { CandidateActivityGroup, CandidateInterviewStatus } from "@type/candidate";
import { interviewStatusNone } from "@constant/app";
import { getCandidateInterviewStatus, getInterviewInsights, getVideo } from "@service/interview";
import { getCandidateActivityHistories } from "@service/job";
import { InterviewInsight, InterviewVideo } from "@type/videoInterview";
import { InterviewPlayer } from "@component/interview-player";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { getUserSubmissionJob } from "@service/cv";
import { EmptyState } from "@component/empty-state";
import SWEExperienceSurvey from "@container/eligibility-check-job";
import AiInterviewTabContent from "@container/drawer/tabs/AiInterviewTabContent";
import ActivityLogTabContent from "@container/drawer/tabs/ActivityLogTabContent";

interface JobApplicationsListProps {
    user_id: string;
    jobApplicantItems: JobApplicantItem[];
}

export default function JobApplicationsList({
    user_id,
    jobApplicantItems,
}: JobApplicationsListProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedInterview, setSelectedInterview] = useState<JobApplicantItem | null>(null);
    const [interviewViewMode, setInterviewViewMode] = useState<"ai-interview" | "form" | "work-auth" | "activity-log">("ai-interview");
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined)
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    const [isLoadingVideo, setLoadingVideo] = useState<boolean>(true);
    const [isLoadingInterviewInsight, setLoadingInterviewInsight] = useState<boolean>(true);
    const [interviewInsights, setInterviewInsights] = useState<InterviewInsight | null>(null);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [formDisplay, setFormDisplay] = useState<UserSubmissionJob | null>(null);
    const [activityGroups, setActivityGroups] = useState<CandidateActivityGroup[]>([]);

    useEffect(() => {
        function fallbackToGeneralInterview() {
            getCandidateInterviewStatus(user_id || "").then((resp) => {
                setInterviewStatus(resp.data);
            })

            getVideo(undefined, "First Time Interview", user_id)
                .then((resp) => {
                    if (resp) {
                        setVideoInterview(resp);
                    }
                    setLoadingVideo(false);
                })
                .catch((error) => {
                    console.error("Failed to get general video interview", error);
                    setLoadingVideo(false);
                });

            getInterviewInsights(undefined, "First Time Interview", user_id)
                .then((res) => {
                    if (res?.insights) {
                        setInterviewInsights(JSON.parse(res.insights));
                    }
                    setLoadingInterviewInsight(false);
                })
                .catch((error) => {
                    console.error("Failed to get general interview insights", error);
                    setLoadingInterviewInsight(false);
                });
        }

        console.log("Selected interview changed:", selectedInterview, "user_id:", user_id);
        if (selectedInterview && user_id) {
            getUserSubmissionJob(selectedInterview.job_id || "", user_id).then((resp) => {
                if (resp.status === 200) {
                    setFormDisplay(resp.data.data);
                }
            })

            if (selectedInterview.assessment_ids && selectedInterview.assessment_ids.filter(assessment => assessment.type === "Interview").length > 0 && user_id) {
                // First try to get user's submission for this specific job
                getUserInterviewSubmission(selectedInterview.assessment_ids?.filter(assessment => assessment.type === "Interview")[0].id as string, user_id)
                    .then((submission: UserInterview) => {
                        if (submission && submission.conversation_id) {
                            // User has a submission for this job, get the specific video
                            console.log("Found interview submission for job:", selectedInterview.job_id, "conversation ID:", submission.conversation_id, "status:", submission.status);

                            setInterviewStatus({
                                status: submission.status || interviewStatusNone,
                                reason: "",
                                updated_at: submission.updated_at || "",
                            })

                            // Get the video using the conversation ID
                            getVideo(submission.conversation_id, undefined, user_id)
                                .then((resp) => {
                                    if (resp) {
                                        setVideoInterview(resp);
                                    }
                                    setLoadingVideo(false);
                                })
                                .catch((error) => {
                                    console.error("Failed to get job-specific video interview", error);
                                    setLoadingVideo(false);
                                });

                            // Get the insights using the conversation ID
                            getInterviewInsights(submission.conversation_id, undefined, user_id)
                                .then((res) => {
                                    if (res?.insights) {
                                        setInterviewInsights(JSON.parse(res.insights));
                                    }
                                    setLoadingInterviewInsight(false);
                                })
                                .catch((error) => {
                                    console.error("Failed to get job-specific interview insights", error);
                                    setLoadingInterviewInsight(false);
                                });
                        } else {
                            // No job-specific submission found, fall back to general interview
                            console.log("No job-specific interview found, falling back to general interview");
                            fallbackToGeneralInterview();
                        }
                    })
                    .catch((error) => {
                        console.error("Failed to get user interview submission", error);
                        fallbackToGeneralInterview();
                    });
            } else {
                fallbackToGeneralInterview();
            }
        }
    }, [selectedInterview, user_id])

    // Activity Log — fetched imperatively when its tab is opened (via the Activity
    // Log button's onClick, or the auto-select fallback below), NOT from a reactive
    // effect. The activity-history endpoint's applicant_id is the candidate's user
    // id (`user_id` here) — the same value the drawer passes as `applicant.applicant_id`.
    // `JobApplicantItem` from this list does not carry an applicant_id.
    const fetchActivityLog = (interview: JobApplicantItem | null) => {
        if (!interview?.job_id || !user_id) return;
        setActivityGroups([]);
        getCandidateActivityHistories(interview.job_id, user_id).then((resp) => {
            if (resp.status === 200) {
                const sortedGroups = [...(resp.data.data || [])].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                setActivityGroups(sortedGroups);
            }
        }).catch((error) => {
            console.error("Failed to get candidate activity histories", error);
        });
    };

    const handleOpenActivityLog = () => {
        setInterviewViewMode("activity-log");
        fetchActivityLog(selectedInterview);
    };

    // When a different application is opened, snap the view mode to the first one
    // that application actually exposes (a toggle button is shown for it), falling
    // back to Activity Log — which is always available. Prevents the detail pane
    // from staying on a mode with no matching content/button. For the Activity Log
    // fallback there's no button click, so kick off its (cached) fetch here too.
    useEffect(() => {
        if (!selectedInterview) return;
        if (selectedInterview.interview_title) {
            setInterviewViewMode("ai-interview");
        } else if (selectedInterview.form_title) {
            setInterviewViewMode("form");
        } else if (selectedInterview.is_has_work_authorization) {
            setInterviewViewMode("work-auth");
        } else {
            handleOpenActivityLog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInterview])

    const filteredInterviews = (jobApplicantItems || []).filter((interview) =>
        interview.job_title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const renderFormScoringExplanation = (question: FormQuestion, answer: string, summary?: string) => {
        const hasValidation =
            (question.type === "Short Answer" && question.correct_answers && question.correct_answers.length > 0) ||
            (question.type === "Multiple Choice" && question.options && question.option_correct_index !== undefined && question.option_correct_index !== null) ||
            (question.type === "Rating Scale" && question.min_rate !== undefined && question.max_rate !== undefined);

        if (!hasValidation) return null;

        return (
            <div className="mt-4">
                {/* Short Answer Explanation */}
                {question.type === "Short Answer" && question.correct_answers && question.correct_answers.length > 0 && (
                    <div className="space-y-2">
                        <p className="text-sm text-gray-900">
                            {summary || "No summary provided by the candidate."}
                        </p>
                        <div className="space-y-1.5">
                            <p className="text-sm text-gray-600">Expected answers:</p>
                            <div className="flex flex-wrap items-center gap-2">
                                {question.correct_answers.map((keyword, idx) => (
                                    <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium"
                                    >
                                        {keyword}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Multiple Choice Explanation */}
                {question.type === "Multiple Choice" && question.options && question.option_correct_index !== undefined && question.option_correct_index !== null && (
                    <div>
                        {answer === question.options[question.option_correct_index] ? (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-white" />
                                </div>
                                <p className="text-sm text-gray-700">
                                    Correct answer selected.
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <X className="w-3 h-3 text-white" />
                                </div>
                                <p className="text-sm text-gray-700">
                                    <span className="font-semibold">Incorrect answer.</span> The correct answer is: &ldquo;{question.options[question.option_correct_index]}&rdquo;
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Rating Scale Explanation */}
                {question.type === "Rating Scale" && question.min_rate !== undefined && question.max_rate !== undefined && (
                    <div>
                        <p className="text-sm text-gray-700">
                            The candidate provided a rating of {answer} out of {question.max_rate},{" "}
                            {Number(answer) >= (question.max_rate * 0.8)
                                ? "indicating a highly positive response."
                                : Number(answer) >= (question.max_rate * 0.6)
                                    ? "indicating a moderately positive response."
                                    : Number(answer) >= (question.max_rate * 0.4)
                                        ? "indicating a negative response."
                                        : "indicating a less favorable response."}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {!selectedInterview ? (
                <div className="space-y-4">
                    {/* Search bar */}
                    <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <input
                            type="text"
                            placeholder="Search by job title..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                    </div>
                    <div className="space-y-3">
                        {filteredInterviews.length === 0 && (
                            <EmptyState
                                title="No job applications found."
                                description={(
                                    <>
                                        Try adjusting your search or <br />filter to find what you're looking for.
                                    </>
                                )}
                            />
                        )}
                        {filteredInterviews.map((interview, index) => (
                            <div
                                key={index}
                                onClick={() => setSelectedInterview(interview)}
                                className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-gray-900 hover:border-blue-600 dark:hover:border-blue-600 transition-all cursor-pointer"
                                style={{
                                    '--hover-border-width': '1.3px'
                                } as React.CSSProperties}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderWidth = '1.3px';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderWidth = '1px';
                                }}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                        <h5 className="font-medium text-sm mb-1">
                                            {interview.job_title}
                                        </h5>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {interview.interview_title}
                                            {interview.form_title && (
                                                <>
                                                    <span className="mx-1.5">•</span>
                                                    {interview.form_title}
                                                </>
                                            )}
                                        </p>
                                    </div>
                                    <Badge
                                        className={cn("whitespace-nowrap inline-flex items-center gap-1.5 cursor-pointer text-sm", getApplicantStatusBadge(convertToLowerSnakeCase(interview.applicant_status)).className)}
                                        style={{
                                            paddingTop: "4px",
                                            paddingBottom: "4px",
                                            paddingLeft: "8px",
                                            paddingRight: "8px",
                                            border:
                                                "0.909091px solid rgba(0, 0, 0, 0)",
                                            borderRadius: "1rem",
                                            boxShadow: "none",
                                        }}
                                    >
                                        {getApplicantStatusBadge(convertToLowerSnakeCase(interview.applicant_status)).label}
                                    </Badge>
                                </div>

                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <Calendar className="h-3.5 w-3.5" />
                                        Applied: {parseFormat(interview.applied_at, "D MMM YYYY")}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    <div className="space-y-4">
                        {/* Header with Back button and Toggle menu */}
                        <div className="flex items-center justify-between gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setSelectedInterview(null);
                                }}
                                className="flex items-center gap-2 -ml-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to Job Applications
                            </Button>

            {/* Toggle menu — Activity Log is always available, so the toggle always
                renders (AI Interview / Form / Eligibility Check appear when the
                application has them). */}
                            {(() => {
                                return (
                                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                                        {selectedInterview?.interview_title && (
                                            <button
                                                onClick={() => setInterviewViewMode("ai-interview")}
                                                className={cn(
                                                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                                    interviewViewMode === "ai-interview"
                                                        ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                                )}
                                            >
                                                AI Interview
                                            </button>
                                        )}
                                        {selectedInterview?.form_title && (
                                            <button
                                                onClick={() => setInterviewViewMode("form")}
                                                className={cn(
                                                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                                    interviewViewMode === "form"
                                                        ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                                )}
                                            >
                                                Form
                                            </button>
                                        )}
                                        {selectedInterview?.is_has_work_authorization && (
                                            <button
                                                onClick={() => setInterviewViewMode("work-auth")}
                                                className={cn(
                                                    "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                                    interviewViewMode === "work-auth"
                                                        ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                                )}
                                            >
                                                Eligibility Check
                                            </button>
                                        )}
                                        <button
                                            onClick={handleOpenActivityLog}
                                            className={cn(
                                                "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                                interviewViewMode === "activity-log"
                                                    ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                            )}
                                        >
                                            Activity Log
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Content based on view mode */}
                        {interviewViewMode === "ai-interview" ? (
                            <AiInterviewTabContent
                                interviewStatus={interviewStatus}
                                interviewInsights={interviewInsights}
                                isLoadingInterviewInsight={isLoadingInterviewInsight}
                                videoDuration={videoDuration}
                            />
                        ) : interviewViewMode === "form" ? (
                            <div className="h-full space-y-4">
                                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-gray-900 dark:text-white flex items-center">
                                            <ClipboardListIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                                            Form Response
                                        </h4>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={!formDisplay}
                                            onClick={() => {
                                                window.open(`/assess/${formDisplay?.assessment_id}/form`, '_blank');
                                            }}
                                            className="h-8 gap-2"
                                        >
                                            <ExternalLinkIcon className="h-4 w-4" />
                                            View Form Details
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Form Name:</span>
                                            <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                {formDisplay?.assessment_name || ""}
                                            </span>
                                        </div>
                                        <div className="text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">Submission Date:</span>
                                            <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                                                {formDisplay?.submission_date ? parseFormat(formDisplay.submission_date, "D MMMM YYYY") : ""}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="space-y-4">
                                        {(formDisplay?.questions || []).map((question, index) => {
                                            const answerData = (formDisplay?.answers || []).find(ans => ans.id === question.id);
                                            const answer = answerData?.content || "";
                                            const summary = answerData?.summary;
                                            return (
                                                <div
                                                    key={question.id}
                                                    className="border border-gray-300 rounded-lg p-4"
                                                    style={{ border: "0.909091px solid #d1d5db" }}
                                                >
                                                    {/* Question Header */}
                                                    <div
                                                        className="flex items-center justify-between mb-4"
                                                        style={{
                                                            paddingTop: "8px",
                                                            paddingBottom: "0px",
                                                            paddingLeft: "16px",
                                                            paddingRight: "16px",
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <div
                                                                className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full text-sm font-medium"
                                                                style={{ border: "1px solid #bfdbfe" }}
                                                            >
                                                                {index + 1}
                                                            </div>
                                                            <span className="text-sm font-semibold text-gray-700">
                                                                {question.type}
                                                            </span>
                                                        </div>
                                                        {/* Show points if question has points */}
                                                        {question.points && question.points !== undefined && (
                                                            <div className="text-base font-semibold text-blue-600 dark:text-blue-400">
                                                                <span>{answerData?.point}/{question.points} {question.points === 1 ? 'point' : 'points'}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Question Content */}
                                                    <div
                                                        className="space-y-4"
                                                        style={{ paddingTop: "0px" }}
                                                    >
                                                        <div
                                                            className="space-y-4"
                                                            style={{
                                                                paddingBottom: "8px",
                                                                paddingRight: "16px",
                                                                paddingLeft: "16px",
                                                            }}
                                                        >
                                                            <div
                                                                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                                                                style={{
                                                                    backgroundColor: "transparent",
                                                                    border: "0px solid rgb(228, 228, 231)",
                                                                    paddingTop: "8px",
                                                                    paddingBottom: "8px",
                                                                    paddingRight: "0px",
                                                                    paddingLeft: "0px",
                                                                }}
                                                            >
                                                                <div
                                                                    className="text-sm text-gray-700"
                                                                    dangerouslySetInnerHTML={{ __html: question.content }}
                                                                />
                                                            </div>

                                                            {/* Candidate Response */}
                                                            <div className="mt-4">
                                                                <label className="text-sm font-semibold text-gray-700 block mb-2">
                                                                    Candidate Response:
                                                                </label>
                                                                {question.type === "Rating Scale" ? (
                                                                    <div className="space-y-4">
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            <span className="text-sm text-gray-600 dark:text-gray-400 text-right mr-2">
                                                                                {question.min_label}
                                                                            </span>
                                                                            {Array.from({ length: (question.max_rate || 5) - (question.min_rate || 1) + 1 }, (_, i) => (question.min_rate || 1) + i).map((value) => (
                                                                                <button
                                                                                    key={value}
                                                                                    type="button"
                                                                                    disabled
                                                                                    className={cn(
                                                                                        "w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all",
                                                                                        value === parseInt(answer)
                                                                                            ? "bg-blue-600 border-blue-600 text-white"
                                                                                            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                                                                                    )}
                                                                                >
                                                                                    {value}
                                                                                </button>
                                                                            ))}
                                                                            <span className="text-sm text-gray-600 dark:text-gray-400 text-left ml-2">
                                                                                {question.max_label}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-center">
                                                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                                                Selected: {answer}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ) : question.type === "Multiple Choice" ? (
                                                                    <Select
                                                                        value={answer || undefined}
                                                                        disabled
                                                                    >
                                                                        <SelectTrigger className="w-full">
                                                                            <SelectValue placeholder="Select an option" />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            {(question.options || []).map((option, idx) => (
                                                                                <SelectItem key={idx} value={option}>
                                                                                    {option}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                ) : (
                                                                            <div className="bg-white border border-gray-200 rounded-md py-1.5 px-2 min-h-[36px]">
                                                                        {answer ? (
                                                                            <p className="text-sm text-gray-900">
                                                                                {answer}
                                                                            </p>
                                                                        ) : (
                                                                            <p className="text-sm text-gray-400 italic">
                                                                                No response provided.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Scoring Explanation */}
                                                            {renderFormScoringExplanation(question, answer, summary)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ) : interviewViewMode === "work-auth" ? (
                            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-6 bg-white dark:bg-gray-900">
                                <SWEExperienceSurvey
                                    is_view
                                    is_full_width
                                    work_authorization_metadata={selectedInterview?.work_authorization_metadata}
                                    elligility_answers={selectedInterview?.elligibility_answers}
                                    location_metadata={selectedInterview?.location_metadata}
                                />
                            </div>
                        ) : (
                            <ActivityLogTabContent activityGroups={activityGroups} />
                        )}
                    </div>
                    <div className="mt-6">
                        <InterviewPlayer
                            isLoadingVideo={isLoadingVideo}
                            interviewStatus={interviewStatus}
                            s3VideoLink={videoInterview?.s3_link || ""}
                            videoInterview={videoInterview as InterviewVideo}
                            setVideoDuration={(newDuration: number) => setVideoDuration(newDuration)}
                            isHide={interviewViewMode !== "ai-interview"}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
